import classNames from "classnames";
import { DateTime } from "luxon";
import {
	EventRSVPLog,
	LogData,
	LogType,
	TimeFinderVoteLog,
} from "../../../../../../common/log_types";
import { PollType } from "../../../../../../common/sesh_types";
import { time_format_utils } from "../../../../../../common/time_format_utils";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { AvailabilityResponse } from "../../../../../../src/api/trpc/time-finder";
import { InlineEmoji } from "../inline_emoji";
import AnonymousNameWithAvatar from "../member_display_name/anonymous_name_with_avatar";
import SimpleMemberDisplayName from "../member_display_name/simple_member_display_name";

type LogTextObject = {
	[key in LogType]: (user_id: string, data: LogData[key]) => JSX.Element;
};

function EventOrPollName(props: { log: { title: string } }) {
	return <span className="font-semibold">{props.log.title}</span>;
}

function SimpleUserName(props: { user_id?: string; hide_avatar?: boolean }) {
	return (
		<span className="font-semibold">
			{props.user_id ? (
				<SimpleMemberDisplayName
					user_id={props.user_id}
					include_avatar={props.hide_avatar ? false : true}
				/>
			) : (
				"unknown"
			)}
		</span>
	);
}

function PollOption(props: {
	has_username_in_it?: boolean;
	log: { option_name: string; poll_type: PollType };
}) {
	let option = props.has_username_in_it
		? props.log.option_name.replace(/ \[added by .*\]$/, "")
		: props.log.option_name;

	option =
		props.log.poll_type === PollType.Standard
			? option
			: time_format_utils.format_time_verbose_date_first(
					DateTime.fromISO(option)
			  );

	return <span className="font-mono font-semibold">{option}</span>;
}

function RSVPOption(props: { log: EventRSVPLog }) {
	return (
		<span className="font-mono font-semibold">
			<InlineEmoji
				option={{
					emoji: props.log.option_emoji,
					custom_emoji_data: props.log.option_is_custom
						? {
								animated: false,
								id: props.log.option_emoji,
						  }
						: undefined,
				}}
			/>
			<p className="ml-1 inline">{props.log.option_name}</p>
		</span>
	);
}

function BoldedAnonymousName() {
	return (
		<span className="font-semibold">
			<AnonymousNameWithAvatar />
		</span>
	);
}

function TimeFinderAvailability(props: { log: TimeFinderVoteLog }) {
	if (
		props.log.user &&
		props.log.user.availability &&
		props.log.user.availability.length > 0
	) {
		return (
			<span className="font-mono font-semibold">
				{props.log.user.availability.map((availability, index) => {
					const dateId = availability.id;
					const dateMs = availability.date_ms;

					if (!dateMs) {
						return null;
					}

					// Convert the milliseconds to a DateTime for formatting
					const dateTime = DateTime.fromMillis(dateMs);

					// Format date differently based on are_times_specified flag
					const formattedDate = props.log.are_times_specified
						? time_format_utils.format_time_verbose_date_first(
								dateTime
						  )
						: dateTime.toUTC().toFormat("MM/dd/yyyy");

					return (
						<span key={dateId}>
							{index > 0 && ", "}
							{formattedDate} as{" "}
							<span className="uppercase">
								{availability.status}
							</span>
						</span>
					);
				})}
			</span>
		);
	}

	return (
		<span className="font-mono font-semibold">availability updated</span>
	);
}

const LogText: LogTextObject = {
	[LogType.EventCreate]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> created an event
		</>
	),
	[LogType.EventEdit]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> edited an event{" "}
		</>
	),

	[LogType.EventDelete]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> deleted an event
		</>
	),
	[LogType.EventAddRSVP]: (user_id, data) => {
		if (data.initiator_id && data.initiator_id !== user_id) {
			return (
				<>
					<SimpleUserName user_id={user_id} /> was RSVPed to{" "}
					<RSVPOption log={data} /> by{" "}
					<SimpleUserName user_id={data.initiator_id} hide_avatar />
				</>
			);
		}
		return (
			<>
				<SimpleUserName user_id={user_id} /> RSVPed to{" "}
				<RSVPOption log={data} />
			</>
		);
	},
	[LogType.EventRemoveRSVP]: (user_id, data) => {
		if (data.initiator_id && data.initiator_id !== user_id) {
			return (
				<>
					<SimpleUserName user_id={user_id} />
					's RSVP for <RSVPOption log={data} /> was removed by{" "}
					<SimpleUserName user_id={data.initiator_id} hide_avatar />
				</>
			);
		}
		return (
			<>
				<SimpleUserName user_id={user_id} /> removed their RSVP for{" "}
				<RSVPOption log={data} />
			</>
		);
	},
	[LogType.EventTemplateFavorite]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> created an event template
		</>
	),
	[LogType.EventTemplateUnfavorite]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> deleted an event template
		</>
	),
	[LogType.PollCreate]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> created a poll
		</>
	),
	[LogType.PollEdit]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> edited a poll
		</>
	),
	[LogType.PollAddOption]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> added option{" "}
			<PollOption log={data} has_username_in_it />
		</>
	),
	[LogType.PollDelete]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> deleted a poll
		</>
	),
	[LogType.PollForceEnd]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> ended a poll
		</>
	),
	[LogType.PollAddVote]: (user_id, data) => {
		const name_element =
			data.is_anonymous === true ? (
				<BoldedAnonymousName />
			) : (
				<SimpleUserName user_id={user_id} />
			);

		return (
			<>
				{name_element} voted for <PollOption log={data} />
			</>
		);
	},

	[LogType.PollRemoveVote]: (user_id, data) => {
		const name_element =
			data.is_anonymous === true ? (
				<BoldedAnonymousName />
			) : (
				<SimpleUserName user_id={user_id} />
			);

		return (
			<>
				{name_element} removed their vote for <PollOption log={data} />
			</>
		);
	},
	[LogType.TimeFinderCreate]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> created a time-finder poll
		</>
	),
	[LogType.TimeFinderEdit]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> edited a time-finder poll
		</>
	),
	[LogType.TimeFinderVote]: (user_id, data) => {
		const name_element = data.is_anonymous ? (
			<BoldedAnonymousName />
		) : (
			<SimpleUserName user_id={user_id} />
		);

		return (
			<>
				{name_element} marked availability for{" "}
				<TimeFinderAvailability log={data as TimeFinderVoteLog} />
			</>
		);
	},
	[LogType.TimeFinderDelete]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> deleted a time-finder poll
		</>
	),
	[LogType.SettingsModify]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} /> modified the server's settings
		</>
	),
	[LogType.SettingsPremiumModify]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} />{" "}
			{data.is_activate ? "activated" : "deactivated"} premium
		</>
	),
	[LogType.ApiTokenGenerate]: (user_id, _data) => (
		<>
			<SimpleUserName user_id={user_id} /> generated an API token
		</>
	),
	[LogType.ApiTokenRegenerate]: (user_id, _data) => (
		<>
			<SimpleUserName user_id={user_id} /> regenerated an API token
		</>
	),
	[LogType.WebhookCreate]: (user_id, _data) => (
		<>
			<SimpleUserName user_id={user_id} /> created a webhook
		</>
	),
	[LogType.WebhookUpdate]: (user_id, _data) => (
		<>
			<SimpleUserName user_id={user_id} /> updated a webhook
		</>
	),
	[LogType.WebhookDelete]: (user_id, _data) => (
		<>
			<SimpleUserName user_id={user_id} /> deleted a webhook
		</>
	),
	[LogType.AdminGuildSeshProUpdated]: (user_id, data) => (
		<>
			<SimpleUserName user_id={user_id} />{" "}
			{data.is_activate ? "activated" : "deactivated"} Sesh Pro (admin)
		</>
	),
};

export function GuildLogText(props: {
	log: RouterInferenceHelpers["guild_logs"]["get"]["output"]["logs"][0];
	is_expanded: boolean;
}) {
	return (
		<div
			className={classNames(
				"inline-block w-full overflow-hidden overflow-ellipsis align-middle",
				{
					"whitespace-nowrap": !props.is_expanded,
					"whitespace-normal": props.is_expanded,
				}
			)}
		>
			{LogText[props.log.type](props.log.userId, props.log.data as any)}
		</div>
	);
}
